import ConstCode from '@/common/constants/ConstCode';
import { Client } from '@stomp/stompjs';
import { chat } from '@/common/service/ApiList';
import ServiceExec from '@/common/utils/ServiceExec';
import { asFormData } from '@/common/utils/ServiceSupport';
import { scrollDown } from '@/views/chat/chatUtil';
import { debounce } from 'lodash';

const { create, getList, detail, chats, saveFile, emitter } = chat;
const { CODE_ENV_LIST, LOGIN_TOKEN_NAME, EMIT_EVENT, isDev } = ConstCode;
const sseUrl = (userNo) => `/eventStream/${userNo}`;
const subscribeUrl = (roomNo) => `/topic/${roomNo}`;
const sendUrl = (roomNo) => `/app/message/${roomNo}`;

let client;
let subscribed;

let sse;
let connected;

const unsubscribeRoom = ({ state, commit }, roomNo, { flushChat = true } = {}) => {
  const { userNo, accessToken } = state.header;

  if (subscribed && roomNo) {
    try {
      subscribed.unsubscribe({
        roomNo,
        userNo: userNo + '',
        accessToken,
        channel: 'CHAT'
      });
    } catch (ignoreException) {}
  }

  subscribed = null;
  if (state.selectedRoom?.roomNo === roomNo) {
    commit('unsubscribe');
    if (flushChat) commit('flushChatData');
  }
};

const apiActions = {
  RESET: ({ commit }) => {},
  FETCH_CHATROOM: async ({ commit, state }, params = {}) => {
    const { data } = await ServiceExec.get(detail, params);

    const { roomNo, profiles, lastBubble } = data;
    commit('setSelectedRoomUser', profiles ?? []);
    // commit('setSelectedRoom', roomNo ?? 0);
    commit('selectRoom', data);
  },
  FETCH_CHATROOMS: async ({ commit, state }) => {
    const { page, limit, roomType, nextId, isLoading } = state.room;
    const { userNo } = state.header;

    if (!userNo || nextId == 0 || isLoading) return;

    const params = { page, limit, roomType, userNo, nextId };
    commit('setRoomLoading', true);

    try {
      const { data } = await ServiceExec.get(getList, params);

      const { pageInfo, list } = data;
      commit('setRoomPage', page + 1);
      commit('setRoomPageNextId', pageInfo.nextId);
      commit('setRoomTotalPage', pageInfo?.totalPage ?? 2);
      commit('setRoomData', list ?? []);
    } finally {
      commit('setRoomLoading', false);
    }
  },
  FETCH_CHAT: async ({ state, commit }) => {
    const { roomNo } = state.selectedRoom;
    const { page, limit, totalPage } = state.chats;

    if (!roomNo) return;
    if (page > totalPage) return;

    const { status, data } = await ServiceExec.get(chats(roomNo), { page, limit });
    const { pageInfo, list } = data;
    commit('setChatTotalPage', pageInfo.totalPage ?? 2);
    commit('setChatPage', page + 1);
    commit('setChatData', list ?? []);
  },
  UPLOAD_IMAGE: async ({ state, commit }, file) => {
    return await ServiceExec.multiPost(saveFile, asFormData({ file }));
  }
};
const stomp = {
  initialize: ({ commit, state, getters }, { userNo, accessToken }) => {
    commit('setHeader', { userNo, accessToken });
    if (client || (client?.active ?? false)) return;
    client = new Client({
      brokerURL: CODE_ENV_LIST.CHAT_SERVER_URL,
      connectHeaders: {
        userNo: userNo,
        accessToken: accessToken,
        tokenType: LOGIN_TOKEN_NAME,
        channel: 'CHAT'
      },
      debug: function (str) {
        if (isDev) console.debug(str);
      },
      reconnectDelay: 3000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000
    });
  },
  connect: ({ commit }) => {
    if (!client) {
      if (isDev) console.debug('NOT INITIALIZED');
      return;
    }
    client.activate();
  },
  disconnect: ({ state, commit }) => {
    const { roomNo } = state.selectedRoom;

    if (roomNo) unsubscribeRoom({ state, commit }, roomNo, { flushChat: false });

    if (client) client.deactivate();
    client = null;
    subscribed = null;
  },

  reconnect: ({ state, commit, getters }, header = { userNo: 0, accessToken: '', channel: 'CHAT' }, roomNo = 0) => {
    setTimeout(async () => {
      await stomp.initialize({ commit, state, getters }, header);
      await stomp.connect({ commit });
      await stomp.subscribe({ state, commit, getters }, roomNo);
    }, 500);
  },

  subscribe: debounce(({ state, getters, commit }, roomNo) => {
    const header = getters['getHeader'];
    if (!client || !client.active) {
      stomp.reconnect({ state, commit, getters }, header, roomNo);
      return;
    }
    const { userNo, accessToken } = state.header;
    commit('subscribe', roomNo);

    const onMessage = (message) => {
      const response = JSON.parse(message.body);
      const { type } = response;
      if (type.toUpperCase() === 'READ') {
        //읽음 처리
        commit('readAllMessages', response.userNo);
      } else {
        //메시지 수신
        commit('appendChat', JSON.parse(message.body));
        //읽었다고 보내기
        client.ack(0, roomNo, {
          userNo: userNo + '',
          accessToken: accessToken,
          channel: 'CHAT'
        });

        commit('updateLastBubble', { latestBubble: JSON.parse(message.body), roomNo });
      }

      scrollDown('chat_box');
    };

    try {
      subscribed = client.subscribe(subscribeUrl(roomNo), onMessage, {
        userNo: userNo + '',
        accessToken: accessToken,
        channel: 'CHAT'
      });

      ServiceExec.patch(chat.read(roomNo, header.userNo));
      commit('readAllMessages', header.userNo);
    } catch {
      actions.initialize({ commit }, { userNo, accessToken });
    }
  }, 300),
  unSubscribe: debounce(({ state, commit }, roomNo) => {
    unsubscribeRoom({ state, commit }, roomNo);
  }, 300),
  send: ({ state, commit, getters }, { msg, file }) => {
    const { userNo, accessToken } = state.header;
    const { roomNo } = state.selectedRoom;
    const { nameKr, nameVn } = getters['getSelectedRoom']?.store ?? {};
    let container = {
      userName: nameVn,
      roomNo,
      userNo,
      content: msg,
      type: file ? 'IMAGE' : 'PLAIN',
      file
    };

    try {
      client.publish({
        destination: sendUrl(roomNo),
        headers: { userNo: userNo + '', accessToken: accessToken, channel: 'CHAT' },
        body: JSON.stringify(container)
      });
    } catch {}
  }
};
const emitterActions = {
  emitterInitialize: ({ state, commit, getters }, { userNo, accessToken }) => {
    commit('setHeader', { userNo, accessToken });
    if (sse || (sse?.active ?? false)) return;
    sse = new Client({
      brokerURL: CODE_ENV_LIST.SSE_SERVER_URL,
      connectHeaders: {
        userNo: userNo,
        accessToken: accessToken,
        tokenType: LOGIN_TOKEN_NAME,
        channel: 'EVENT_STREAM'
      },
      debug: function (str) {
        if (isDev) console.debug('SSE :' + str);
      },
      reconnectDelay: 3000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000
    });
    return;
  },
  emitterConnect: ({ commit }) => {
    if (!sse) {
      isDev ? console.error('NOT INITIALIZED') : '';
      return;
    }
    sse.activate();
    return;
  },
  emitterDisconnect: ({ state, commit }) => {
    const { userNo, accessToken } = state.header;
    const connectedClient = connected;
    const emitterClient = sse;

    if (userNo && connectedClient) {
      connectedClient.unsubscribe({
        userNo: userNo + '',
        accessToken,
        channel: 'EVENT_STREAM'
      });
    }

    connected = null;
    if (emitterClient) emitterClient.deactivate();
    sse = null;
  },
  emitterReconnect: ({ state, commit, getters }) => {
    setTimeout(async () => {
      const header = getters['getHeader'];
      await emitterActions.emitterInitialize({ commit, state, getters }, header);
      await emitterActions.emitterConnect({ commit });
      await emitterActions.emitterSubscribe({ state, commit, getters });
    }, 500);
  },

  emitterSubscribe: debounce(({ state, commit, getters }) => {
    if (!sse || !sse.active) {
      emitterActions.emitterReconnect({ state, commit, getters });
      return;
    }
    const onMessage = (message) => {
      const path = window.location.pathname;

      const container = JSON.parse(message.body);
      const { chat_status, order_status, store_disabled, new_chat, leave_chat } = ConstCode.SSE_TYPE;

      switch (container?.eventType) {
        case chat_status:
          // if (container?.badge) {
          //   commit('setBubbleCounts', container?.badge);
          // }
          if (container?.latest) {
            const { roomNo, count, latest } = container?.latest;
            commit('setRoomUnreadBubbleCount', {
              roomNo: roomNo,
              count: count
            });
            commit('updateLastBubble', {
              latestBubble: latest,
              roomNo: roomNo
            });
          }
          break;
        case order_status:
          commit('setLobbyOrderState', container.order);
          commit('setRoomOrderState', container.order);
          break;
        case store_disabled:
          commit('setLobbyStoreChatState', container.disabled);
          commit('setRoomStoreChatState', container.disabled);
          break;
        case new_chat:
          commit('appendRoom', container?.newChat?.room);
          break;
        case leave_chat:
          const leaveChat = container?.leaveChat;
          const leaveRoomNo = typeof leaveChat === 'object' ? leaveChat?.roomNo : leaveChat;

          if (!leaveRoomNo) break;
          if (getters['getSelectedRoom']?.roomNo === leaveRoomNo) {
            stomp.unSubscribe({ state, commit }, leaveRoomNo);
          }

          commit('leaveRoomAtRoom', { roomNo: leaveRoomNo });
          commit('leaveRoomAtLobby', { roomNo: leaveRoomNo });
          break;
        default:
          break;
      }
    };
    const { userNo, accessToken } = getters['getHeader'];
    connected = sse.subscribe(sseUrl(userNo), onMessage, {
      userNo: userNo + '',
      accessToken: accessToken,
      channel: 'EVENT_STREAM'
    });
  }, 300),
  emitterUnSubscribe: debounce(({ state, commit, getters }) => {
    const { userNo, accessToken } = getters['getHeader'];
    if (!sse?.connected) return;
    if (connected) {
      connected.unsubscribe({
        //request unsub

        userNo: userNo + '',
        accessToken: accessToken,
        channel: 'EVENT_STREAM'
      }); //if above not work}
      // commit('unsubscribe');
      // commit('flushChatData');
    }
  }, 300)
};

export const actions = {
  ...stomp,
  ...apiActions,
  ...emitterActions
};

export default actions;
